import styled from 'styled-components';
import { AsyncMetaState } from '@mews/framework';
import { Container } from '@optimus-web/core';
import { ErrorHandler } from './ErrorHandler';
import { Loader } from './Loader';
import { OverlayedLoader } from './OverlayedLoader';

interface LoaderProps {
    isLoaderAbsolute?: boolean;
}

interface ErrorHandlerProps {
    onDismiss?: () => void;
    onTryAgain?: () => void;
    errorMessage?: React.ReactElement;
    errorTitle?: React.ReactElement;
}

export interface AsyncContainerProps extends LoaderProps, ErrorHandlerProps, AsyncMetaState {}

export const asyncContainerFactory =
    ({ ContainerElement = AsyncContainerElement, ContentElement = ContentWrapper, withOverlayedLoader = false } = {}) =>
    <T extends object>(WrappedComponent: React.ComponentType<T>) => {
        const AsyncContainer = ({
            isFetching,
            hasFailed,
            errorMessage,
            errorTitle,
            onDismiss,
            onTryAgain,
            isLoaderAbsolute,
            ...other
        }: AsyncContainerProps & T) => {
            let content;
            let key;
            if (hasFailed) {
                key = 'error';
                content = (
                    <ErrorHandler
                        errorTitle={errorTitle}
                        errorMessage={errorMessage}
                        onTryAgain={onTryAgain}
                        onDismiss={onDismiss}
                    />
                );
            } else if (isFetching && !withOverlayedLoader) {
                key = 'loader';
                content = (
                    <Container padding="200">
                        <Loader />
                    </Container>
                );
            } else {
                key = 'component';
                content = <WrappedComponent {...(other as T)} />;
            }

            return (
                <ContainerElement isLoaderAbsolute={isLoaderAbsolute}>
                    <ContentElement key={key}>
                        {content}
                        {withOverlayedLoader ? <OverlayedLoader show={isFetching} /> : null}
                    </ContentElement>
                </ContainerElement>
            );
        };

        return AsyncContainer;
    };

const AsyncContainerElement = styled.div<LoaderProps>`
    ${(props) =>
        props.isLoaderAbsolute &&
        `
        position: absolute;
    `}
    top: 50%;
    width: 100%;
`;

const ContentWrapper = styled.div`
    position: relative;
`;

export const withAsyncContainer = asyncContainerFactory();
